import { useState, useEffect, useRef } from "react";

/* ─── GLOBAL STYLES ────────────────────────────────────────────────── */
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0a0a0a;
    --bg2: #111111;
    --bg3: #161616;
    --border: rgba(255,255,255,0.07);
    --accent: #c8f135;
    --accent2: #a8d020;
    --text: #f0f0f0;
    --text2: #aaaaaa;
    --text3: #666666;
    --card: #141414;
    --radius: 16px;
    --radius-sm: 8px;
    --cursor-size: 12px;
    --cursor-ring: 40px;
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    line-height: 1.6;
    cursor: none;
    overflow-x: hidden;
  }

  h1,h2,h3,h4,h5 { font-family: 'Space Grotesk', sans-serif; line-height: 1.1; }

  a { color: inherit; text-decoration: none; }
  img { max-width: 100%; display: block; }

  /* ── Custom cursor ── */
  #cursor-dot {
    position: fixed; top: 0; left: 0; pointer-events: none; z-index: 9999;
    width: var(--cursor-size); height: var(--cursor-size);
    background: var(--accent); border-radius: 50%;
    transform: translate(-50%,-50%);
    transition: transform .1s, width .2s, height .2s, opacity .3s;
    mix-blend-mode: difference;
  }
  #cursor-ring {
    position: fixed; top: 0; left: 0; pointer-events: none; z-index: 9998;
    width: var(--cursor-ring); height: var(--cursor-ring);
    border: 1.5px solid rgba(200,241,53,.5); border-radius: 50%;
    transform: translate(-50%,-50%);
    transition: transform .18s ease, width .25s, height .25s, opacity .3s;
  }
  body:hover #cursor-dot { opacity: 1; }

  /* ── Scrollbar ── */
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 2px; }

  /* ── Reveal animations ── */
  .reveal { opacity: 0; transform: translateY(32px); transition: opacity .7s ease, transform .7s ease; }
  .reveal.visible { opacity: 1; transform: translateY(0); }
  .reveal-left { opacity: 0; transform: translateX(-40px); transition: opacity .7s ease, transform .7s ease; }
  .reveal-left.visible { opacity: 1; transform: translateX(0); }
  .reveal-right { opacity: 0; transform: translateX(40px); transition: opacity .7s ease, transform .7s ease; }
  .reveal-right.visible { opacity: 1; transform: translateX(0); }

  /* ── Noise overlay ── */
  body::before {
    content:''; position:fixed; inset:0; pointer-events:none; z-index:1000;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    opacity: .4;
  }

  /* ── Nav ── */
  nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 500;
    padding: 20px 5%;
    display: flex; align-items: center; justify-content: space-between;
    transition: background .4s, backdrop-filter .4s, padding .3s;
  }
  nav.scrolled {
    background: rgba(10,10,10,.85);
    backdrop-filter: blur(20px);
    padding: 14px 5%;
    border-bottom: 1px solid var(--border);
  }
  .nav-logo {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.3rem; font-weight: 700;
    color: var(--text);
  }
  .nav-logo span { color: var(--accent); }
  .nav-links { display: flex; gap: 36px; align-items: center; }
  .nav-links a {
    font-size: .85rem; font-weight: 500; letter-spacing: .05em; text-transform: uppercase;
    color: var(--text2); position: relative; transition: color .2s;
  }
  .nav-links a::after {
    content:''; position:absolute; bottom:-4px; left:0; width:0; height:1.5px;
    background: var(--accent); transition: width .25s;
  }
  .nav-links a:hover { color: var(--text); }
  .nav-links a:hover::after { width: 100%; }
  .nav-resume {
    background: var(--accent) !important; color: #0a0a0a !important;
    padding: 8px 20px; border-radius: 6px; font-weight: 600 !important;
    transition: opacity .2s !important;
  }
  .nav-resume:hover { opacity: .85 !important; }
  .nav-resume::after { display:none !important; }
  .hamburger { display:none; flex-direction:column; gap:5px; cursor:pointer; }
  .hamburger span { width:24px; height:2px; background: var(--text); border-radius:2px; transition:.3s; }

  /* ── Hero ── */
 .hero {
  min-height: 100vh; padding: 0 5%;
  display: flex; align-items: center;
  position: relative; overflow: hidden;
  animation: heroBg 12s ease infinite;
}
@keyframes heroBg {
  0%   { background: #0a0a0a; }
  25%  { background: #0d0a14; }
  50%  { background: #0a0f10; }
  75%  { background: #0f0a0a; }
  100% { background: #0a0a0a; }
}
  .hero-bg-grid {
    position:absolute; inset:0; pointer-events:none;
    background-image: linear-gradient(var(--border) 1px, transparent 1px),
                      linear-gradient(90deg, var(--border) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 50%, black, transparent);
  }
  .hero-glow {
    position:absolute; top:20%; right:-5%;
    width:600px; height:600px; border-radius:50%;
    background: radial-gradient(circle, rgba(200,241,53,.12) 0%, transparent 70%);
    pointer-events:none;
    animation: pulse 4s ease-in-out infinite;
  }
  @keyframes pulse { 0%,100%{opacity:.7;transform:scale(1)} 50%{opacity:1;transform:scale(1.08)} }
  .hero-inner { max-width: 800px; position:relative; z-index:2; padding-top: 100px; text-align: center; margin: 0 auto; }
  .hero-badge {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(200,241,53,.08); border: 1px solid rgba(200,241,53,.2);
    padding: 6px 16px; border-radius: 50px; font-size: .8rem; font-weight: 500;
    color: var(--accent); margin-bottom: 28px;
    animation: fadeDown .6s ease forwards;
  }
  .hero-badge::before {
    content:''; width:7px; height:7px; background: var(--accent); border-radius:50%;
    animation: blink 1.4s ease-in-out infinite;
  }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.2} }
  .hero-sub {
    font-size: 1rem; color: var(--text2); margin-bottom: 16px; font-weight: 400;
    animation: fadeUp .6s .1s ease both;
  }
  .hero-title {
    font-size: clamp(2.8rem, 7vw, 5.5rem); font-weight: 800;
    line-height: 1.05; margin-bottom: 28px;
    animation: fadeUp .7s .2s ease both;
  }
  .hero-title em { font-style:normal; color: var(--accent); }
 .hero-desc {
  font-size: 1.05rem; color: var(--text2); max-width: 520px; margin: 0 auto 40px;
  animation: fadeUp .7s .35s ease both;
}

 .hero-btns { display: flex; gap: 16px; flex-wrap: wrap; justify-content: center; animation: fadeUp .7s .5s ease both; }
  .btn-primary {
    background: var(--accent); color: #0a0a0a;
    padding: 13px 32px; border-radius: 8px; font-weight: 700; font-size: .9rem;
    letter-spacing: .03em; transition: transform .2s, opacity .2s;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .btn-primary:hover { transform: translateY(-2px); opacity: .9; }
  .btn-outline {
    border: 1.5px solid var(--border); color: var(--text);
    padding: 13px 32px; border-radius: 8px; font-weight: 600; font-size: .9rem;
    transition: border-color .2s, background .2s;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .btn-outline:hover { border-color: var(--accent); background: rgba(200,241,53,.05); }
  .hero-scroll {
    position: absolute; bottom: 40px; left: 50%; transform: translateX(-50%);
    display: flex; flex-direction:column; align-items:center; gap:8px;
    color: var(--text3); font-size: .75rem; letter-spacing:.1em; text-transform:uppercase;
    animation: fadeUp .7s .8s ease both;
  }
  .scroll-line {
    width: 1px; height: 50px;
    background: linear-gradient(to bottom, var(--accent), transparent);
    animation: scrollDown 1.5s ease-in-out infinite;
  }
  @keyframes scrollDown { 0%{transform:scaleY(0);transform-origin:top} 50%{transform:scaleY(1);transform-origin:top} 51%{transform:scaleY(1);transform-origin:bottom} 100%{transform:scaleY(0);transform-origin:bottom} }

  @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeDown { from{opacity:0;transform:translateY(-16px)} to{opacity:1;transform:translateY(0)} }

  /* ── Section base ── */
  section { padding: 100px 5%; position:relative; }
  .section-label {
    font-size: .75rem; font-weight: 600; letter-spacing: .15em; text-transform: uppercase;
    color: var(--accent); margin-bottom: 12px;
  }
  .section-title {
    font-size: clamp(2rem, 5vw, 3.2rem); font-weight: 800; margin-bottom: 24px;
  }
  .section-desc { color: var(--text2); max-width: 600px; font-size: 1rem; margin-bottom: 60px; }
  .divider { height: 1px; background: var(--border); margin: 0 5%; }

  /* ── About ── */
  .about-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center;
  }
  .about-text p { color: var(--text2); margin-bottom: 20px; line-height: 1.8; }
  .about-quote {
    border-left: 3px solid var(--accent);
    padding: 16px 24px; margin: 28px 0;
    background: rgba(200,241,53,.04);
    border-radius: 0 8px 8px 0;
    color: var(--text2); font-style:italic; font-size: .95rem; line-height:1.7;
  }
    .about-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
  position: relative;
  z-index: 2;
}
 .about-img-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: var(--bg3);
  border-radius: 50%;
  width: min(470px, 80vw);
  height: min(470px, 80vw);
  overflow: hidden;
  margin: 0 auto;
}
  .about-img-wrap::before {
    content:''; position:absolute; inset:0; z-index:2;
    background: linear-gradient(135deg, rgba(200,241,53,.15) 0%, transparent 60%);
  }
  .about-img-placeholder {
    width:100%; height:100%;
    background: linear-gradient(135deg, #1a1a1a 0%, #222 100%);
    display:flex; align-items:center; justify-content:center;
    color: var(--text3); font-size: 3rem;
  }
  .about-img-border {
    position:absolute; inset:-2px; border: 1px solid var(--border); border-radius:var(--radius); z-index:3; pointer-events:none;
  }

  /* ── Services ── */
  .services-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; }
  .service-card {
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 28px;
    transition: border-color .3s, transform .3s, background .3s;
    cursor: none;
  }
  .service-card:hover { border-color: rgba(200,241,53,.3); transform: translateY(-4px); background: #181818; }
  .service-icon {
    width: 44px; height: 44px; border-radius: 10px;
    background: rgba(200,241,53,.1); display:flex; align-items:center; justify-content:center;
    margin-bottom: 20px; font-size: 1.3rem; color: var(--accent);
  }
  .service-card h3 { font-size: 1.05rem; margin-bottom: 10px; }
  .service-card p { color: var(--text2); font-size: .875rem; line-height: 1.7; }

  /* ── Timeline (Education/Experience) ── */
  .timeline { position:relative; padding-left: 32px; }
  .timeline::before {
    content:''; position:absolute; left: 8px; top:0; bottom:0;
    width: 1.5px; background: var(--border);
  }
  .timeline-item { position: relative; margin-bottom: 44px; }
  .timeline-dot {
    position: absolute; left: -28px; top: 6px;
    width: 12px; height: 12px; border-radius: 50%;
    background: var(--accent); border: 2px solid var(--bg);
    box-shadow: 0 0 0 4px rgba(200,241,53,.15);
  }
  .timeline-date {
    font-size: .75rem; font-weight: 600; letter-spacing:.08em; text-transform:uppercase;
    color: var(--accent); margin-bottom: 6px;
  }
  .timeline-badge {
    display: inline-block; background: rgba(200,241,53,.1);
    border: 1px solid rgba(200,241,53,.2); color: var(--accent);
    padding: 2px 10px; border-radius: 20px; font-size: .7rem; font-weight: 600;
    margin-left: 10px; vertical-align: middle;
  }
  .timeline-title { font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; }
  .timeline-org { color: var(--accent); font-size: .875rem; margin-bottom: 6px; font-weight: 500; }
  .timeline-loc { color: var(--text3); font-size: .8rem; margin-bottom: 10px; }
  .timeline-desc { color: var(--text2); font-size: .875rem; line-height: 1.7; }
  .edu-exp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; }

  /* ── Skills ── */
  .skills-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
  .skill-card {
    background: var(--card); border: 1px solid var(--border); border-radius: var(--radius-sm);
    padding: 20px; display:flex; align-items:center; gap: 14px;
    transition: border-color .25s, transform .25s;
  }
  .skill-card:hover { border-color: rgba(200,241,53,.25); transform: translateY(-3px); }
  .skill-icon { width: 36px; height: 36px; border-radius: 8px; object-fit:contain; flex-shrink:0; background:#1e1e1e; padding:4px; }
  .skill-info h4 { font-size: .9rem; margin-bottom: 2px; }
  .skill-level { font-size: .72rem; color: var(--text3); }
  .skill-level.advanced { color: var(--accent); }
  .skill-level.intermediate { color: #88c0d0; }
  .skill-level.beginner { color: var(--text3); }

  /* ── Portfolio ── */
  .portfolio-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; flex-wrap: wrap; gap: 16px; }
  .portfolio-stats { display: flex; gap: 32px; }
  .stat { text-align:center; }
  .stat-num { font-family:'Space Grotesk',sans-serif; font-size: 2.5rem; font-weight: 800; color: var(--accent); line-height:1; }
  .stat-label { font-size: .75rem; color: var(--text3); text-transform:uppercase; letter-spacing:.1em; }
  .portfolio-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; margin-top: 32px; }
  .portfolio-card {
    background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
    overflow: hidden; position:relative; cursor: none;
    transition: transform .3s, border-color .3s;
    aspect-ratio: 4/3;
  }
  .portfolio-card:hover { transform: scale(1.02); border-color: rgba(200,241,53,.25); }
  .portfolio-card img { width:100%; height:100%; object-fit:cover; transition: transform .5s; }
  .portfolio-card:hover img { transform: scale(1.06); }
  .portfolio-overlay {
    position:absolute; inset:0;
    background: linear-gradient(to top, rgba(0,0,0,.85) 0%, transparent 50%);
    padding: 20px; display:flex; flex-direction:column; justify-content:flex-end;
  }
  .portfolio-tag {
    position:absolute; top:14px; left:14px;
    background: var(--accent); color: #0a0a0a;
    padding: 3px 10px; border-radius: 4px; font-size: .7rem; font-weight: 700; text-transform:uppercase;
  }
  .portfolio-title { font-size: 1rem; font-weight: 700; margin-bottom: 4px; }
  .portfolio-cat { font-size: .78rem; color: rgba(255,255,255,.6); }
  .portfolio-placeholder {
    width:100%; height:100%;
    background: linear-gradient(135deg, #1c1c1c, #222);
    display:flex; align-items:center; justify-content:center;
    color: var(--text3); font-size: 2.5rem;
  }

  /* ── FAQ ── */
  .faq-list { max-width: 780px; }
 .faq-item {
  border: 1px solid var(--border); border-radius: var(--radius-sm);
  margin-bottom: 12px; transition: border-color .25s;
}
  .faq-item.open { border-color: rgba(200,241,53,.25); }
 .faq-q {
  padding: 20px 24px; display:flex; align-items:center; justify-content:space-between;
  cursor: pointer; font-weight: 600; font-size: .95rem;
  background: var(--card); transition: background .2s;
}
  .faq-q:hover { background: #181818; }
  .faq-icon { color: var(--accent); font-size: 1.2rem; transition: transform .3s; flex-shrink:0; }
  .faq-item.open .faq-icon { transform: rotate(45deg); }
 .faq-a {
  max-height: 0; overflow:hidden;
  transition: max-height .45s ease;
  background: var(--card);
}
.faq-item.open .faq-a { max-height: 400px; }
  .faq-a-inner { padding: 0 24px 20px; color: var(--text2); font-size: .9rem; line-height: 1.7; }

  /* ── Contact ── */
  .contact-inner {
    background: var(--bg3); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 70px 60px; text-align:center; position:relative; overflow:hidden;
  }
  .contact-inner::before {
    content:''; position:absolute; top:-80px; left:50%; transform:translateX(-50%);
    width: 400px; height: 400px; border-radius:50%;
    background: radial-gradient(circle, rgba(200,241,53,.1) 0%, transparent 70%);
    pointer-events:none;
  }
  .contact-inner h2 { font-size: clamp(1.8rem, 4vw, 3rem); margin-bottom: 20px; }
  .contact-inner p { color: var(--text2); max-width: 560px; margin: 0 auto 40px; font-size: 1rem; }
  .contact-btns { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }

  /* ── Footer ── */
  footer {
    background: var(--bg2); border-top: 1px solid var(--border);
    padding: 60px 5% 32px;
  }
  .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 60px; margin-bottom: 48px; }
  .footer-brand p { color: var(--text2); font-size: .875rem; margin-top: 14px; line-height: 1.7; max-width: 280px; }
  .footer-col h4 { font-size: .8rem; text-transform:uppercase; letter-spacing:.12em; color: var(--text3); margin-bottom: 20px; }
  .footer-links { list-style:none; }
  .footer-links li { margin-bottom: 10px; }
  .footer-links a { color: var(--text2); font-size: .875rem; transition: color .2s; }
  .footer-links a:hover { color: var(--accent); }
  .social-links { display: flex; gap: 12px; margin-top: 20px; flex-wrap: wrap; }
  .social-link {
    width: 38px; height: 38px; border-radius: 8px;
    background: var(--card); border: 1px solid var(--border);
    display:flex; align-items:center; justify-content:center;
    color: var(--text2); font-size: .85rem;
    transition: border-color .2s, color .2s, transform .2s;
  }
  .social-link:hover { border-color: var(--accent); color: var(--accent); transform: translateY(-2px); }
  .footer-bottom { border-top: 1px solid var(--border); padding-top: 24px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
  .footer-bottom p { color: var(--text3); font-size: .8rem; }

  /* ── Mobile menu ── */
  .mobile-menu {
    display:none; position:fixed; inset:0; z-index:490;
    background: rgba(10,10,10,.97); flex-direction:column;
    align-items:center; justify-content:center; gap:36px;
  }
  .mobile-menu.open { display:flex; }
  .mobile-menu a { font-family:'Space Grotesk',sans-serif; font-size: 2rem; font-weight: 700; color: var(--text2); transition:color .2s; }
  .mobile-menu a:hover { color: var(--accent); }

  /* ── Prog skills tabs ── */
  .prog-tabs { display:flex; gap:10px; flex-wrap:wrap; margin-bottom: 28px; }
  .prog-tab {
    padding: 7px 18px; border-radius: 6px; font-size: .8rem; font-weight: 600;
    border: 1px solid var(--border); color: var(--text2); cursor:none;
    transition: all .2s;
  }
  .prog-tab.active, .prog-tab:hover { background: var(--accent); color: #0a0a0a; border-color: var(--accent); }
  .prog-skills-grid { display:flex; flex-wrap:wrap; gap:10px; }
  .prog-skill-tag {
    background: var(--card); border: 1px solid var(--border);
    padding: 8px 16px; border-radius: 6px; font-size: .85rem; color: var(--text2);
    transition: border-color .2s, color .2s;
  }
  .prog-skill-tag:hover { border-color: var(--accent); color: var(--text); }

  /* ── Responsive ── */
  @media (max-width: 900px) {
    .about-grid, .edu-exp-grid { grid-template-columns: 1fr; gap: 48px; }
    .about-img-wrap { width: min(380px, 75vw); height: min(380px, 75vw); }
    .footer-grid { grid-template-columns: 1fr 1fr; }
    .nav-links { display:none; }
    .hamburger { display:flex; }
    .contact-inner { padding: 48px 28px; }
    .hero-glow { width: 400px; height: 400px; }
  }
  @media (max-width: 600px) {
    section { padding: 72px 4%; }
    .footer-grid { grid-template-columns: 1fr; }
    .hero-btns { flex-direction:column; align-items: center; }
    .hero-btns a { width: 100%; max-width: 280px; justify-content: center; }
    .portfolio-stats { gap: 20px; }
    .about-img-wrap { width: min(300px, 70vw); height: min(300px, 70vw); }
    .contact-inner { padding: 36px 20px; }
    .contact-btns { flex-direction: column; align-items: center; }
    .contact-btns a { width: 100%; max-width: 280px; justify-content: center; }
    .stat-num { font-size: 2rem; }
    .portfolio-grid { grid-template-columns: 1fr; }
    .services-grid { grid-template-columns: 1fr; }
    .skills-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); }
    .hero-glow { width: 260px; height: 260px; top: 10%; right: -15%; }
    .footer-grid { gap: 32px; }
    .footer-bottom { flex-direction: column; text-align: center; }
    .faq-q { padding: 16px 16px; font-size: .85rem; }
    .faq-a-inner { padding: 0 16px 16px; }
  }
  @media (max-width: 400px) {
    .about-img-wrap { width: 80vw; height: 80vw; }
    .hero-title { font-size: clamp(2rem, 10vw, 2.8rem); }
    .section-title { font-size: clamp(1.6rem, 7vw, 2.4rem); }
  }
`;

/* ─── DATA ────────────────────────────────────────────────────────── */
const services = [
  {
    icon: "🎨",
    title: "Graphic Design",
    desc: "Crafting visually compelling designs including logos, branding systems, posters, and digital artwork that communicate your message with clarity and impact."
  },
  {
    icon: "🌐",
    title: "Web Development",
    desc: "Building modern, responsive, and high-performing websites using React, JavaScript, and modern web technologies focused on functionality and user experience."
  },
  {
    icon: "🎬",
    title: "Video Editing",
    desc: "Professional video editing with Premiere Pro and DaVinci Resolve, delivering clean, engaging, and story-driven visual content."
  },
  {
    icon: "✨",
    title: "Motion Graphics",
    desc: "Creating dynamic motion graphics and animations using After Effects to enhance storytelling and elevate visual communication."
  },
  {
    icon: "🏷️",
    title: "Brand Identity Design",
    desc: "Developing strong and cohesive brand identities including logos, color systems, typography, and visual guidelines that define your brand presence."
  },
  {
    icon: "📱",
    title: "UI/UX Design",
    desc: "Designing intuitive and user-centered interfaces using Figma, focused on improving usability, engagement, and overall digital experience."
  },
  {
    icon: "🖼️",
    title: "Social Media Design",
    desc: "Creating high-impact social media visuals tailored for engagement, consistency, and brand growth across all platforms."
  },
  {
    icon: "⚙️",
    title: "Full-Stack Solutions",
    desc: "End-to-end digital solutions combining frontend design and backend development to deliver complete, scalable web applications."
  },
  {
  icon: "📊",
  title: "Data Entry Services",
  desc: "Providing accurate and efficient data entry solutions including data collection, data mining, and data extraction from various sources. Expertise in PDF conversion, Excel and Word document handling, data organization, and spreadsheet management with strong attention to detail, speed, and confidentiality."
}
];

const education = [
   {
    date: "2022",
    title: "Computer Science",
    org: "University of the Punjab",
    desc: "Studied Computer Science with focus on programming, algorithms, and system design, building strong analytical and problem-solving skills."  },
  {
    date: "2023",
    title: "Full Stack Web Development",
    org: "PNY Institute",
    desc: "Studied core academic subjects with strong analytical and problem-solving development, along with foundational knowledge supporting technical and creative skills."
  },
  
];

const experience = [
  
   {
    date: "Feb 2023 – April 2024",
    current: true,
    title: "Full Stack Web Developer",
    org: "Code Tech Studio",
    loc: "Lahore",
    desc: "Full-stack developer with 3 years of experience building web applications using React and Laravel/Node.js. Experienced in developing scalable systems, APIs, and database-driven platforms across HR, logistics, and content-based projects.",  },
 
  {
    date: "May 2024 – Present",
    title: "Full Stack Web Developer",
    org: "TravelsHook Pvt Ltd",
    loc: "Lahore",
    desc: "Supported development, assisted in content preparation and client coordination, and gained exposure to digital marketing and engagement strategies."
  }
];

const softwareSkills = [
  { name: "React.js", level: "Advanced" },
  { name: "JavaScript", level: "Advanced" },
  { name: "HTML5 & CSS3", level: "Advanced" },
  { name: "Tailwind CSS", level: "Advanced" },
  { name: "Figma", level: "Advanced" },
  { name: "Microsoft Office", level: "Advanced" },

  { name: "Adobe Photoshop", level: "Advanced" },
  { name: "Adobe Illustrator", level: "Advanced" },
  { name: "UI/UX Design", level: "Advanced" },
  { name: "Responsive Web Design", level: "Advanced" },
  { name: "Git & GitHub", level: "Intermediate" },

  { name: "Node.js", level: "Advanced" },
  { name: "Express.js", level: "Advanced" },
  { name: "Laravel", level: "Intermediate" },
  { name: "REST APIs", level: "Advanced" },
  { name: "MySQL", level: "Advanced" },
  { name: "MongoDB", level: "Intermediate" },
  { name: "Authentication (JWT)", level: "Intermediate" },
];
const progSkillsData = {
  Core: ["HTML5", "CSS3", "JavaScript"],
  Frameworks: ["React", "Next.js", "Tailwind CSS"],
  Languages: ["JavaScript (ES6+)", "TypeScript", "Python"],
  Tools: ["Git", "GitHub", "VS Code", "Figma","Canva"],
  Backend: ["Node.js", "Express", "REST API","PHP","Laravel"],
  Docs: ["Markdown", "JSDoc", "README"],
  Deployment: ["Vercel", "Netlify", "GitHub Pages"],
};

const portfolioItems = [
  {
    title: "Live Fares",
    cat: "Web Development",
    type: "Travel Platform",
    link: "https://livefares.co/",
    image: "livefare.png"
  },
   {
    title: "Code Tech Studio",
    cat: "Web Development",
    type: "Software House",
    link: "https://www.codetechstudio.com/",
    image: "codetech.png"
  },
 
  {
    title: "Travel Shook",
    cat: "Traveling Company",
    type: "Business Site",
    link: "https://travelshook.co.uk/",
    image: "travelshook.jpeg"
  },
 
  {
    title: "Altech Engeering",
    cat: "Engeering",
    type: "Construction Website",
    link: "https://altecengineers.com/",
    image: "altec.png"
  },
  {
    title: "Mate Cleaning Services",
    cat: "Cleaning Business",
    type: "Business Site",
    link: "https://matecleaningservices.com.au/",
    image: "mate.png"
  },
 {
    title: "RJ Logistics",
    cat: "Logistics Website",
    type: "Business Site",
    link: "https://www.rjlogisticsllc.info/",
    image: "rj.png"
  },
   {
    title: "Kim Heavy Equipment LLC",
    cat: "Dubai based Heavy Equipment Site",
    type: "Business Site",
    link: "https://www.kimheavyequipment.com/",
    image: "kim.png"
  },
  {
    title: "Infinity Auto Ship",
    cat: "Logistics Website",
    type: "Business Website",
    link: "https://infinityautoship.netlify.app/",
    image: "infinity.png"
  },
 
  {
    title: "Legacy Express Logistics",
    cat: "Logistics System",
    type: "Transport Website",
    link: "https://legacyexpress.netlify.app/",
    image: "legacy.png"
  },
  {
    title: "Crisferrita Transportation",
    cat: "Logistics",
    type: "Fleet Management",
    link: "https://www.crisferritainterstatetransportation.com/",
    image: "crisferrita.png"
  },
  
  
 
];

const faqs = [
  {
    q: "What services do you offer as a Graphic Designer & Web Developer?",
    a: "I provide professional graphic design and web development services including brand identity design, social media graphics, website UI/UX design, responsive website development, landing pages, portfolio websites, and modern digital experiences tailored to your brand."
  },
  {
    q: "What is your typical project turnaround time?",
    a: "Timelines depend on the project scope and complexity. Graphic design projects usually take 2–5 days, while complete website design and development projects may take 1–3 weeks. Fast-track delivery can also be arranged if required."
  },
  {
    q: "Do you offer revisions during the project?",
    a: "Yes, absolutely. I include revision rounds in every project to ensure the final design or website aligns perfectly with your vision and expectations."
  },
  {
    q: "Will my website be responsive and mobile-friendly?",
    a: "Yes. Every website I create is fully responsive and optimized for desktops, tablets, and mobile devices to provide a seamless user experience across all screen sizes."
  },
  {
    q: "Can you redesign or improve an existing website?",
    a: "Definitely. I can redesign outdated websites, improve UI/UX, optimize performance, and create a more modern, visually engaging, and user-friendly experience."
  },
  {
    q: "What technologies and tools do you use?",
    a: "I work with modern technologies and professional design tools including React, JavaScript, HTML, CSS, Tailwind CSS, Figma, Adobe Photoshop, Illustrator, and other industry-standard development and design software."
  },
];

/* ─── HOOKS ───────────────────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal,.reveal-left,.reveal-right").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

function useCounter(target, visible) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = target / 40;
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 30);
    return () => clearInterval(timer);
  }, [visible, target]);
  return count;
}

/* ─── COMPONENTS ──────────────────────────────────────────────────── */

function Cursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  useEffect(() => {
    const onMove = e => {
      if (dotRef.current) { dotRef.current.style.left = e.clientX + "px"; dotRef.current.style.top = e.clientY + "px"; }
      if (ringRef.current) { ringRef.current.style.left = e.clientX + "px"; ringRef.current.style.top = e.clientY + "px"; }
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  return (
    <>
      <div id="cursor-dot" ref={dotRef} />
      <div id="cursor-ring" ref={ringRef} />
    </>
  );
}

function Navbar({ menuOpen, setMenuOpen }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <>
      <nav className={scrolled ? "scrolled" : ""}>
        <a href="#" className="nav-logo">Portfolio<span>.</span></a>
        <div className="nav-links">
          {["#about","#services","#portfolio","#contact"].map((href, i) => (
            <a key={i} href={href}>{href.slice(1).charAt(0).toUpperCase() + href.slice(2)}</a>
          ))}
          <a href="/resume.pdf" download="Fatima_Resume.pdf" className="nav-resume">Resume</a>
        </div>
        <div className="hamburger" onClick={() => setMenuOpen(o => !o)}>
          <span /><span /><span />
        </div>
      </nav>
      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
        {["#about","#services","#portfolio","#contact"].map((href, i) => (
          <a key={i} href={href} onClick={() => setMenuOpen(false)}>
            {href.slice(1).charAt(0).toUpperCase() + href.slice(2)}
          </a>
        ))}
      </div>
    </>
  );
}

function Hero() {
  return (
    <section className="hero" id="home">
      <div className="hero-bg-grid" />
      <div className="hero-glow" />
      <div className="hero-inner">
        <div className="hero-badge">Available for Work</div>
        <p className="hero-sub">Hi, I'm <b>Fatima Shafaqat</b></p>
        <h1 className="hero-title">A Full Stack<em>Web Developer</em></h1>
        <p className="hero-desc">
          Passionate in creating visually captivating design that make a difference.
          Transforming ideas into beautiful visuals that leave a lasting impression.
        </p>
        <div className="hero-btns">
          <a href="#portfolio" className="btn-primary">View Full Portfolio →</a>
          <a href="/resume.pdf" download="Fatima_Resume.pdf" className="btn-outline">⬇ View Resume</a>
        </div>
      </div>
      <div className="hero-scroll">
        <span>Scroll</span>
        <div className="scroll-line" />
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about">
      <div className="about-grid">
        <div className="about-text reveal-left">
          <p className="section-label">Who I am</p>
          <h2 className="section-title">About Me</h2>
     
       <p>
  I'm <strong>Fatima Shafaqat</strong>, a passionate Full Stack Web Developer  with a strong foundation
  in building modern, responsive web applications and crafting visually compelling digital experiences.
  I enjoy blending clean code with creative design to bring ideas to life in a meaningful and impactful way.
</p>

<blockquote className="about-quote">
  "I believe great design goes beyond aesthetics — it solves real problems through clarity, usability, and creativity.
  As a developer and designer, I strive to build experiences that are both visually engaging and functionally seamless.
  I am eager to collaborate, continuously learn, and grow in this ever-evolving digital landscape."
</blockquote>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <a href="/resume.pdf" download="Fatima_Resume.pdf" className="btn-primary">⬇ View Resume</a>
            <a href="#portfolio" className="btn-outline">View Work</a>
          </div>
        </div>
      <div className="about-img-wrap reveal-right">
  <img src="./pic.jpeg" alt="about" className="about-img" />
  <div className="about-img-border" />
</div>
      </div>
    
    </section>
  );
}

function Services() {
  return (
    <section id="services">
      <div className="divider"  />
      <p className="section-label reveal">Comprehensive creative solutions</p>
      <h2 className="section-title reveal">Services I Offer</h2>
      <p className="section-desc reveal">
        From concept to execution, tailored to bring your vision to life.
      </p>
      <div className="services-grid">
        {services.map((s, i) => (
          <div className="service-card reveal" key={i} style={{ transitionDelay: `${i * 60}ms` }}>
            <div className="service-icon">{s.icon}</div>
            <h3>{s.title}</h3>
            <p>{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function EduExp() {
  return (
    <section>
      <div className="edu-exp-grid">
        <div>
          <p className="section-label reveal">My Academic Journey</p>
          <h2 className="section-title reveal">Education</h2>
          <div className="timeline reveal">
            {education.map((e, i) => (
              <div className="timeline-item" key={i}>
                <div className="timeline-dot" />
                <p className="timeline-date">{e.date}</p>
                <h3 className="timeline-title">{e.title}</h3>
                <p className="timeline-org">{e.org}</p>
                <p className="timeline-desc">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="section-label reveal">My Professional Journey</p>
          <h2 className="section-title reveal">Experience</h2>
          <div className="timeline reveal">
            {experience.map((e, i) => (
              <div className="timeline-item" key={i}>
                <div className="timeline-dot" />
                <p className="timeline-date">
                  {e.date}
                  {e.current && <span className="timeline-badge">Current</span>}
                </p>
                <h3 className="timeline-title">{e.title}</h3>
                <p className="timeline-org">{e.org}</p>
                <p className="timeline-loc">{e.loc}</p>
                <p className="timeline-desc">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Skills() {
  const [activeTab, setActiveTab] = useState("Core");
  const tabs = Object.keys(progSkillsData);

  return (
    <section>
      <div className="divider" style={{ margin: "0 0 0px" }} />
      <p className="section-label reveal">Softwares I Work With</p>
      <h2 className="section-title reveal">My Skills</h2>
      <div className="skills-grid">
        {softwareSkills.map((s, i) => (
          <div className="skill-card reveal" key={i} style={{ transitionDelay: `${i * 50}ms` }}>
            <div className="skill-icon" style={{ background: "#1e1e1e", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.4rem" }}>
              🛠️
            </div>
            <div className="skill-info">
              <h4>{s.name}</h4>
              <p className={`skill-level ${s.level.toLowerCase()}`}>{s.level}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="section-label reveal" style={{ marginTop: 72 }}>Technologies & Tools I Use for Development</p>
      <h2 className="section-title reveal">Programming Skills</h2>
      <div className="prog-tabs reveal">
        {tabs.map(t => (
          <button key={t} className={`prog-tab ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>{t}</button>
        ))}
      </div>
      <div className="prog-skills-grid reveal">
        {progSkillsData[activeTab].map((s, i) => (
          <div className="prog-skill-tag" key={i}>{s}</div>
        ))}
      </div>
    </section>
  );
}

function Portfolio() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  const projects = useCounter(8, vis);
  const categories = useCounter(2, vis);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVis(true);
      },
      { threshold: 0.3 }
    );

    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="portfolio">
      <div className="divider" style={{ margin: "0 0 0px" }} />

      <div className="portfolio-header">
        <div>
          <p className="section-label reveal">Web Development & Design</p>
          <h2 className="section-title reveal">My Projects</h2>
        </div>

        <div className="portfolio-stats" ref={ref}>
          <div className="stat">
            <div className="stat-num">{projects}+</div>
            <div className="stat-label">Live Projects</div>
          </div>

          <div className="stat">
            <div className="stat-num">{categories}+</div>
            <div className="stat-label">Categories</div>
          </div>
        </div>
      </div>

      <a
        href="#"
        className="btn-outline reveal"
        style={{ display: "inline-flex", marginBottom: 8 }}
      >
        View All Work →
      </a>

      <div className="portfolio-grid">
        {portfolioItems.map((p, i) => (
          <a
            key={i}
            href={p.link}
            target="_blank"
            rel="noreferrer"
            className="portfolio-card reveal"
            style={{ textDecoration: "none", transitionDelay: `${i * 80}ms` }}
          >
            {/* Small preview image (replace emoji with thumbnail if available) */}
            <div className="portfolio-thumb">
              <img src={p.image} alt={p.title} />
            </div>

            <div className="portfolio-overlay">
              <div className="portfolio-tag">{p.type}</div>
              <h3 className="portfolio-title">{p.title}</h3>
              <p className="portfolio-cat">{p.cat}</p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section>
      <div className="divider" style={{ margin: "0 0 0px" }} />
      <p className="section-label reveal">Common questions about my services</p>
      <h2 className="section-title reveal">Frequently Asked Questions</h2>
      <div className="faq-list reveal">
        {faqs.map((f, i) => (
          <div className={`faq-item ${open === i ? "open" : ""}`} key={i}>
            <div className="faq-q" onClick={() => setOpen(open === i ? null : i)}>
              <span>{f.q}</span>
              <span className="faq-icon">{open === i ? "−" : "+"}</span>
            </div>
            <div className="faq-a">
              <div className="faq-a-inner">{f.a}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
function Contact() {
  return (
    <section id="contact">
      <div className="divider" style={{ margin: "0 0 0px" }} />
      <div className="contact-inner reveal">
        <p className="section-label" style={{ marginBottom: 12 }}>Have a project in mind?</p>
    <h2>Let’s Build Something Amazing Together!</h2>
<p>
  Combining creative graphic design with modern web development to create visually engaging
  and impactful digital experiences. Let’s collaborate to bring your ideas to life with
  stunning designs and seamless websites tailored to your vision.

        </p>
        <div className="contact-btns">
          <a href="#portfolio" className="btn-primary">View My Work →</a>
          <a href="mailto:fatimamalikk066@gmail.com" className="btn-outline">📧 Email Me</a>
          <a href="tel:+92 329 9408478" className="btn-outline">📞 Call Now</a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-grid">
        <div className="footer-brand">
          <div className="nav-logo">Portfolio<span style={{ color: "var(--accent)" }}>.</span></div>
          <p> A passionate web developer and graphic designer with a strong foundation
  in building modern, responsive web applications and crafting visually compelling digital experiences.</p>
        
        </div>
        <div className="footer-col">
          <h4>Quick Links</h4>
          <ul className="footer-links">
            {["Home","About","Services","Portfolio","Work","Resume"].map(l => (
              <li key={l}><a href="#">{l}</a></li>
            ))}
          </ul>
        </div>
        <div className="footer-col">
          <h4>Contact Info</h4>
          <ul className="footer-links">
            <li><a href="fatimamalikk066@gmail.com">fatimamalikk066@gmail.com</a></li>
            <li><a href="tel:+92 329 9408478">+92 329 9408478</a></li>
          </ul>
           <h4>Address</h4>
             <ul className="footer-links">
            <li><a>Lahore, Punjab</a></li>
          
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© {new Date().getFullYear()} Fatima Shafaqat. All rights reserved.</p>
        <p style={{ color: "var(--text3)", fontSize: ".8rem" }}>Designed & Developed with ❤️</p>
      </div>
    </footer>
  );
}

/* ─── APP ─────────────────────────────────────────────────────────── */
export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  useReveal();

  return (
    <>
      <style>{globalStyles}</style>
      <Cursor />
      <Navbar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <main>
        <Hero />
        <About />
        <Services />
        <EduExp />
        <Skills />
        <Portfolio />
        <FAQ />
        <Contact />
      </main>
      <Footer />
    </>
  );
}